<template>
    <div class="input-control-wrapper">
        <input :id="control.uniqueId"
               :type="control.typeAttribute"
               :inputmode="computedInputMode"
               :autocomplete="computedAutocomplete"
               :class="[controlFieldClass, prefillClass]"
               :value="displayValue"
               :name="control.name || control.uniqueId"
               :placeholder="effectivePlaceholder"
               :readonly="isPrefillLocked"
               @input="handleInput($event.target.value)"
               @blur="handleBlur"
        />
        <small v-if="control.teethNumbersEmpty && !isBuilderMode" class="teeth-empty-note">
            Note: this field is to be filled with teeth #'s. If blank, no scheduled appointments were found for this patient.
        </small>
    </div>
</template>

<script>
    import {CONTROL_FIELD_EXTEND_MIXIN} from "../../mixins/control-field-extend-mixin";
    import {formatPhoneForDisplay, formatSsnForDisplay} from "../../libraries/format-field-value";

    /**
     * InputControl doesn't need to setValue. We can eventually assign the bind the value into the <input>
     * Safe safe...
     */
    export default {
        name: "InputControl",
        mixins: [CONTROL_FIELD_EXTEND_MIXIN],

        data() {
            return {
                // Set the moment the patient types into a prefill field that arrived EMPTY. Without
                // it the field would re-lock on the first keystroke, because the value it is typing
                // is the same value `isPrefillLocked` inspects.
                prefillFieldEdited: false,
            };
        },

        computed: {
            /**
             * A `prefillPatientName` field locks ONLY when the prefill actually supplied a name.
             *
             * Why (2026-09-25): the lock used to be unconditional. Opened from a link with no
             * patient context — a QR code, a website link, or an A2P compliance reviewer opening
             * the form cold — there is nothing to prefill, so the field rendered readonly AND
             * empty: impossible to complete. Twilio's reviewer reported exactly that ("certain
             * fields, such as Name and Date, are currently locked or non-functional") on Brester
             * Dentistry's consent form.
             *
             * It is not cosmetic. 25 controls across the fleet pair `prefillPatientName` with a
             * `required` rule; on those, a generic link produced a form that could never be
             * submitted — a required field nobody could fill.
             *
             * The lock still applies in the normal case, where a patient opens their own link and
             * the name is filled in for them: that value stays protected from edits, which is the
             * behaviour the prefill was built for.
             */
            isPrefillLocked() {
                if (!this.control.prefillPatientName || this.isBuilderMode) return false;
                if (this.prefillFieldEdited) return false;
                return String(this.value == null ? '' : this.value).trim() !== '';
            },

            /**
             * Determines if we're in builder mode (preview) vs renderer mode (patient-facing form)
             * In builder mode, the parent component is typically SectionContainer or similar
             * In renderer mode, there's a readOnly prop passed down
             */
            isBuilderMode() {
                // Check if parent has readOnly prop - if it's undefined, we're likely in builder mode
                // FormRenderer passes readOnly explicitly (true or false), FormBuilder doesn't
                return this.$parent && this.$parent.readOnly === undefined;
            },

            /**
             * Staff review / PDF render. Nothing the patient does applies here, and the
             * on-blur tidy-up must never rewrite a stored submission value.
             */
            isReadOnlyContext() {
                if (this.$attrs && (this.$attrs.readOnly === true || this.$attrs.viewSubmissionMode === true)) {
                    return true;
                }
                return !!(this.$parent && (this.$parent.readOnly === true || this.$parent.viewSubmissionMode === true));
            },

            /**
             * The rule types this control carries, lowercased — the engine matches
             * case-insensitively, so a hand-edited "isphone" is a real, working rule and
             * has to drive the keyboard and the blur formatting too.
             * @returns {String[]}
             */
            controlRuleTypes() {
                const rules = Array.isArray(this.control.validations) ? this.control.validations : [];
                return rules
                    .filter(rule => rule && typeof rule.ruleType === 'string')
                    .map(rule => rule.ruleType.toLowerCase());
            },

            hasIsPhoneRule() {
                return this.controlRuleTypes.includes('isphone');
            },

            hasIsSSNRule() {
                return this.controlRuleTypes.includes('isssn');
            },

            /**
             * The placeholder to show - when prefillPatientName is enabled and in builder mode,
             * show the preview text, otherwise use the normal localized placeholder
             */
            effectivePlaceholder() {
                if (this.control.prefillPatientName && this.isBuilderMode) {
                    return '[Last Name], [First Name]';
                }
                if (this.control.prefillTeethNumbers && this.isBuilderMode) {
                    return '[Teeth #\'s]';
                }
                return this.localizedPlaceholder;
            },

            /**
             * The value to display - when a prefill option is enabled and in builder mode,
             * show the preview text as the value
             */
            displayValue() {
                if (this.control.prefillPatientName && this.isBuilderMode) {
                    return '[Last Name], [First Name]';
                }
                if (this.control.prefillTeethNumbers && this.isBuilderMode) {
                    return '[Teeth #\'s]';
                }
                return this.value;
            },

            /**
             * CSS class for prefill styling - different for builder vs renderer mode
             */
            prefillClass() {
                if (this.control.prefillPatientName) {
                    if (this.isBuilderMode) return 'prefill-patient-name-preview';
                    // Only style it as locked when it IS locked — an empty prefill field is a
                    // normal editable input and must not look greyed out and unavailable.
                    return this.isPrefillLocked ? 'prefill-patient-name-readonly' : '';
                }
                if (this.control.prefillTeethNumbers) {
                    return this.isBuilderMode ? 'prefill-teeth-preview' : 'prefill-teeth-editable';
                }
                return '';
            },

            /**
             * Determines the inputmode attribute for mobile keyboards
             * Based on field name patterns or explicit inputMode setting
             */
            computedInputMode() {
                // Use explicit inputMode if set on control
                if (this.control.inputMode) {
                    return this.control.inputMode;
                }

                // A format RULE beats the name/label guess below: the office has said in so
                // many words what this field holds, whatever it happens to be called.
                // ("Contact" carrying isPhone still gets the phone keypad.)
                if (this.hasIsPhoneRule) {
                    return 'tel';
                }
                if (this.hasIsSSNRule) {
                    return 'numeric';
                }

                // Auto-detect based on field name (case-insensitive)
                const name = (this.control.name || '').toLowerCase();
                const label = (this.control.label || '').toLowerCase();
                const fieldText = name + ' ' + label;

                // Numeric fields - show number pad.
                // zip/postal deliberately NOT here: postal codes are
                // alphanumeric outside the US (Canada "T2V 0E4", UK, NL...)
                // and inputmode="tel" hides letters on mobile keyboards —
                // Canadian patients couldn't type their postal code at all
                // (SmileBAR 2026-06-11). They keep autocomplete="postal-code"
                // below, which is what actually helps fill them.
                // Mirrored in cypress/node-tests/forms-input-mode.test.js.
                if (fieldText.match(/phone|mobile|cell|fax|ssn|ss#|social.*security|age|year|extension/)) {
                    return 'tel';
                }

                // Email fields
                if (fieldText.match(/email|e-mail/)) {
                    return 'email';
                }

                // URL fields
                if (fieldText.match(/website|url|link/)) {
                    return 'url';
                }

                // Numeric-only fields (no phone formatting)
                if (fieldText.match(/number|amount|quantity|count|total|price|cost/)) {
                    return 'numeric';
                }

                // Default to text
                return 'text';
            },

            /**
             * Determines autocomplete attribute for better mobile UX
             */
            computedAutocomplete() {
                // Use explicit autocomplete if set
                if (this.control.autocomplete) {
                    return this.control.autocomplete;
                }

                // Same precedence as computedInputMode. SSN is deliberately 'off' — a
                // browser must never be invited to store or autofill one.
                if (this.hasIsPhoneRule) {
                    return 'tel';
                }
                if (this.hasIsSSNRule) {
                    return 'off';
                }

                const name = (this.control.name || '').toLowerCase();
                const label = (this.control.label || '').toLowerCase();
                const fieldText = name + ' ' + label;

                // Common autocomplete mappings
                if (fieldText.match(/first.*name|fname|given.*name/)) return 'given-name';
                if (fieldText.match(/last.*name|lname|family.*name|surname/)) return 'family-name';
                if (fieldText.match(/middle.*name|mname|middle.*initial/)) return 'additional-name';
                if (fieldText.match(/full.*name|name/) && !fieldText.match(/first|last|middle/)) return 'name';
                if (fieldText.match(/email|e-mail/)) return 'email';
                if (fieldText.match(/phone|mobile|cell/)) return 'tel';
                if (fieldText.match(/street|address.*1|address.*line.*1/)) return 'address-line1';
                if (fieldText.match(/address.*2|address.*line.*2|apt|suite|unit/)) return 'address-line2';
                if (fieldText.match(/city/)) return 'address-level2';
                if (fieldText.match(/state|province/)) return 'address-level1';
                if (fieldText.match(/zip|postal/)) return 'postal-code';
                if (fieldText.match(/country/)) return 'country-name';
                if (fieldText.match(/birth.*date|dob|date.*of.*birth/)) return 'bday';

                return 'off';
            }
        },

        methods: {
            /**
             * Handle input — blocks updates only while a prefill field is actually LOCKED.
             *
             * `readonly` already stops typing in a real browser, so this is the belt-and-braces
             * half. It used to refuse every keystroke whenever `prefillPatientName` was set, which
             * meant a field the prefill left empty could not be filled by anyone. See
             * `isPrefillLocked` above.
             */
            handleInput(val) {
                if (this.isPrefillLocked) {
                    return;
                }
                if (this.control.prefillPatientName && !this.isBuilderMode) {
                    // An empty prefill field the patient is now filling in themselves: remember
                    // that, so the first keystroke does not immediately re-lock the field.
                    this.prefillFieldEdited = true;
                }
                this.updateValue(val);
            },

            /**
             * Tidy a VALID phone / SSN the moment the patient leaves the field:
             * "5555551234" -> "(555) 555-1234", "123456789" -> "123-45-6789".
             *
             * Only when the control carries the matching rule, only on the patient-facing
             * form (never in the builder preview, never in staff review / the PDF), and
             * only when the value is already valid — an invalid value stays EXACTLY as
             * typed so the patient can see their own mistake next to the red message.
             */
            handleBlur(event) {
                if (this.isBuilderMode || this.isReadOnlyContext) {
                    return;
                }
                if (!this.hasIsPhoneRule && !this.hasIsSSNRule) {
                    return;
                }

                const raw = event && event.target && typeof event.target.value === 'string'
                    ? event.target.value
                    : this.value;
                const current = raw === null || raw === undefined ? '' : String(raw);
                if (current.trim() === '') {
                    return;
                }

                const formatted = this.hasIsPhoneRule
                    ? formatPhoneForDisplay(current)
                    : formatSsnForDisplay(current);

                if (typeof formatted === 'string' && formatted !== current) {
                    this.handleInput(formatted);
                }
            }
        }
    }
</script>

<style scoped>
/* Preview styling for Pre-fill Patient Name in FormBuilder */
.prefill-patient-name-preview {
    background-color: #f0fdf4 !important;
    border-color: #86efac !important;
    color: #166534 !important;
    font-style: italic;
}

/* Readonly styling for Pre-filled Patient Name in FormRenderer (patient-facing) */
.prefill-patient-name-readonly {
    background-color: #f5f5f5 !important;
    font-weight: bold !important;
    color: #333 !important;
    cursor: not-allowed;
    pointer-events: none;
    user-select: none;
}

/* Preview styling for Pre-fill Teeth #'s in FormBuilder */
.prefill-teeth-preview {
    background-color: #f0fdf4 !important;
    border-color: #86efac !important;
    color: #166534 !important;
    font-style: italic;
}

/* Editable styling for Pre-filled Teeth #'s in FormRenderer (patient-facing) */
.prefill-teeth-editable {
    border-left: 3px solid #86efac !important;
}

/* Empty state note when no appointment found */
.teeth-empty-note {
    display: block;
    color: #888;
    font-style: italic;
    margin-top: 4px;
    font-size: 0.8em;
}

.input-control-wrapper {
    width: 100%;
}
</style>